import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    Text,
    Card,
    CardBody,
    Input,
    FormLabel,
    Select,
    Progress,
    Divider,
    Flex,
    VStack,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    useToast,
    Collapse,
    Grid,
    Tag
} from '@chakra-ui/react';
import {
    FiUpload,
    FiFileText,
    FiSend,
    FiCheckCircle,
    FiXCircle,
    FiAlertCircle,
    FiDownload,
    FiPlay,
    FiPause,
    FiX,
    FiArrowLeft,
    FiArrowRight,
    FiUsers,
    FiAlertTriangle,
    FiActivity,
    FiChevronDown,
    FiChevronUp,
    FiEye
} from 'react-icons/fi';
import axios from 'axios';
import { getApiHeaders } from '../../../../utils/DefaultFunction';
import { DeliveryChannels } from './DeliveryChannels';

import { CampaignActivityLog } from './CampaignActivityLog';
import { decodeHtmlEntities } from '../../../TemplateManager/CreateTemplate/EmailDesigner/htmlExport';
import EmailPreviewModal from '../../../TemplateManager/CreateTemplate/EmailPreviewModal';

/**
 * ET_048: an email template's body is a full HTML document, and it was rendered with
 * <Text>, so the preview showed the raw markup as visible text. Resolve the template's
 * HTML here — same field order the campaign Review step uses — and return '' for
 * plain-text (SMS/WhatsApp) templates so those keep the plain-text preview.
 */
const EMAIL_TAG_RE = /<\/?(html|body|table|tr|td|div|p|br|span|img|a|h[1-6]|style)\b/i;

function templateEmailHtml(template) {
    if (!template) return '';
    const explicit = template.html_code || template.emailHtml || template.email_code || template.email_html || '';
    const raw = String(explicit).trim() ? explicit : (template.message || '');
    const decoded = decodeHtmlEntities(String(raw || ''));
    return decoded && EMAIL_TAG_RE.test(decoded) ? decoded : '';
}

export function CampaignModal({
    isOpen,
    onClose,
    templates,
    channels,
    onCampaignComplete,
    existingCampaign,
}) {
    const [currentStep, setCurrentStep] = useState(1);
    const [campaignName, setCampaignName] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedChannels, setSelectedChannels] = useState([]); // now an array
    const [uploadedFile, setUploadedFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [validationProgress, setValidationProgress] = useState(0);
    const [isValidationComplete, setIsValidationComplete] = useState(false);
    const [validationError, setValidationError] = useState(null);
    const [csvColumns, setCsvColumns] = useState([]);
    const [missingVariables, setMissingVariables] = useState([]);
    const [variableMappings, setVariableMappings] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [invalidContacts, setInvalidContacts] = useState(0);
    const [duplicates, setDuplicates] = useState(0);
    const [emptyVariables, setEmptyVariables] = useState(0);
    const [isSending, setIsSending] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [sentCount, setSentCount] = useState(0);
    const [failedCount, setFailedCount] = useState(0);
    const [pendingCount, setPendingCount] = useState(0);
    const [queueCount, setQueueCount] = useState(0);
    const [campaignLogs, setCampaignLogs] = useState([]);
    const [showLogs, setShowLogs] = useState(true);
    const [isCompleted, setIsCompleted] = useState(false);
    const [uploadedFileData, setUploadedFileData] = useState(null);
    const [csvId, setCsvId] = useState(null);
    const [isPauseLoading, setIsPauseLoading] = useState(false);
    const [isResumeLoading, setIsResumeLoading] = useState(false);
    const [isCancelLoading, setIsCancelLoading] = useState(false);
    const [csvValidationErrors, setCsvValidationErrors] = useState(null);
    const [showTemplatePreview, setShowTemplatePreview] = useState(false);
    const [showDuplicateDetails, setShowDuplicateDetails] = useState(false);
    const [showEmptyVarDetails, setShowEmptyVarDetails] = useState(false);
    const [showInvalidDetails, setShowInvalidDetails] = useState(false);
    const toast = useToast();
    const progressIntervalRef = useRef(null);
    const pollingIntervalRef = useRef(null);

    // ─── helpers ────────────────────────────────────────────────────────────────
    const getActiveCsvId = () =>
        csvId || uploadedFileData?.id || uploadedFileData?._id || uploadedFileData?.file_id;

    // ordered channel names from selectedChannels array
    const getOrderedChannelNames = () =>
        selectedChannels
            .map(id => channels.find(c => c.id == id)?.name)
            .filter(Boolean);



    const resetWizard = () => {
        setCurrentStep(1);
        setCampaignName('');
        setSelectedTemplate(null);
        setSelectedChannels([]);
        setUploadedFile(null);
        setVariableMappings([]);
        setCsvColumns([]);
        setIsSending(false);
        setIsPaused(false);
        setIsCompleted(false);
        setSentCount(0);
        setFailedCount(0);
        setPendingCount(0);
        setQueueCount(0);
        setCampaignLogs([]);
        setIsValidating(false);
        setIsValidationComplete(false);
        setValidationProgress(0);
        setValidationError(null);
        setMissingVariables([]);
        setCsvValidationErrors(null);
        setShowTemplatePreview(false);
        setShowDuplicateDetails(false);
        setShowEmptyVarDetails(false);
        setShowInvalidDetails(false);
        setUploadedFileData(null);
        setCsvId(null);
        if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }
        if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
    };

    useEffect(() => {
        if (existingCampaign && isOpen) handleExistingCampaign(existingCampaign);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingCampaign, isOpen, csvId]);

    const handleExistingCampaign = (campaign) => {
        setCampaignLogs(Array.isArray(campaign.logs) ? campaign.logs : []);

        if (campaign.status_id === 'Draft') {
            const template = templates.find(t => t.id === campaign.template_id);
            setSelectedTemplate(template);
            setCsvId(campaign._id);
            setTotalRows(campaign.total_rows);
            setVariableMappings(campaign.variable_mappings || []);
            const columns = campaign.variable_mappings.map(vm => ({ name: vm.csvColumn, sampleValue: 'From CSV' }));
            setCsvColumns(columns);
            setUploadedFileData(campaign);
            setIsValidationComplete(true);
            setCurrentStep(2);
        } else if (
            ['processing', 'pending', 'running', 'stopped', 'paused', 'cancelled'].includes(campaign.status_id)
        ) {
            const template = templates.find(t => t.id === campaign.template_id);
            setSelectedTemplate(template);
            setCsvId(campaign._id);
            setTotalRows(campaign.total_rows);
            setSentCount(campaign.success_count || 0);
            setFailedCount(campaign.error_count || 0);
            setPendingCount(campaign.total_rows - campaign.process_count);
            setQueueCount(campaign.que_count || 0);
            const isPausedStatus = campaign.status_id === 'stopped' || campaign.status_id === 'paused';
            setIsPaused(isPausedStatus);
            setIsSending(true);
            setCurrentStep(3);
            if (!isPausedStatus) startStatusPolling();
        } else if (campaign.status_id === 'completed') {
            const template = templates.find(t => t.id === campaign.template_id);
            setSelectedTemplate(template);
            setCsvId(campaign._id);
            setTotalRows(campaign.total_rows);
            setSentCount(campaign.success_count || 0);
            setFailedCount(campaign.error_count || 0);
            setPendingCount(0);
            setIsCompleted(true);
            setIsSending(false);
            setCurrentStep(3);
        }
    };

    const handleClose = () => {
        resetWizard();
        onClose();
        if (onCampaignComplete) onCampaignComplete();
    };

    // ─── template variable extraction ───────────────────────────────────────────
    const extractVariables = (message) => {
        if (!message) return [];
        const regex = /\$(\w+)/g;
        const matches = [];
        let match;
        while ((match = regex.exec(message)) !== null) {
            if (!matches.includes(match[1])) matches.push(match[1]);
        }
        return matches;
    };

    const parseCSVHeaders = (csvText) => {
        const lines = csvText.split('\n');
        if (lines.length === 0) return [];
        return lines[0].split(',').map(h => h.trim().replace(/['"]/g, ''));
    };

    // ─── drag & drop ─────────────────────────────────────────────────────────────
    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = () => setIsDragging(false);
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file && file.name.endsWith('.csv')) processFile(file);
    };
    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file && file.name.endsWith('.csv')) processFile(file);
        e.target.value = '';
    };

    // ─── file processing (local validation only) ─────────────────────────────────
    const processFile = async (file) => {
        setUploadedFile(file);
        setIsValidating(true);
        setValidationProgress(0);
        setIsValidationComplete(false);
        setValidationError(null);
        setMissingVariables([]);

        if (!selectedTemplate) {
            setValidationError('Please select a template first');
            setIsValidating(false);
            toast({ title: 'Error', description: 'Please select a template first', status: 'error', duration: 3000, isClosable: true });
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const headers = parseCSVHeaders(e.target.result);
            const templateVariables = extractVariables(selectedTemplate.message);
            const missing = templateVariables.filter(v => !headers.includes(v));

            if (missing.length > 0) {
                setMissingVariables(missing);
                setValidationError(`Missing variables in CSV: ${missing.join(', ')}`);
                setIsValidating(false);
                setValidationProgress(0);
                toast({ title: 'Validation Error', description: `Missing columns: ${missing.join(', ')}`, status: 'error', duration: 5000, isClosable: true });
                return;
            }

            setCsvColumns(headers.map(h => ({ name: h, sampleValue: 'Sample Value' })));
            setIsValidating(false);
            setIsValidationComplete(true);
        };
        reader.onerror = () => {
            setValidationError('Failed to read CSV file');
            setIsValidating(false);
            toast({ title: 'Error', description: 'Failed to read CSV file', status: 'error', duration: 3000, isClosable: true });
        };
        reader.readAsText(file);
    };

    // ─── upload CSV to API ────────────────────────────────────────────────────────
    const uploadCSV = async (file, headers) => {
        try {
            let progress = 0;
            progressIntervalRef.current = setInterval(() => {
                progress += 20;
                if (progress >= 80) {
                    clearInterval(progressIntervalRef.current);
                    progressIntervalRef.current = null;
                    progress = 80;
                }
                setValidationProgress(progress);
            }, 300);

            const formData = new FormData();
            formData.append('file', file);
            formData.append('template_id', selectedTemplate.id);
            formData.append('name', campaignName);
            const activeCsvId = getActiveCsvId();
            if (activeCsvId) {
                formData.append('csv_id', activeCsvId);
            }

            const uploadResponse = await axios.post(
                `${process.env.REACT_APP_API_URL}/csv/msg-upload`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data', ...getApiHeaders() } }
            );

            if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }

            if (uploadResponse.status === 200 && uploadResponse.data) {
                setValidationProgress(100);
                setIsValidating(false);
                setIsValidationComplete(true);
                setUploadedFileData(uploadResponse.data);
                setCsvId(uploadResponse.data.id || uploadResponse.data._id || uploadResponse.data.file_id);
                setCsvColumns(headers.map(h => ({ name: h, sampleValue: 'Sample Value' })));
                setTotalRows(uploadResponse.data.totalRows);
                setInvalidContacts(uploadResponse.data.invalid_contacts || 0);
                setDuplicates(uploadResponse.data.duplicates || 0);
                setEmptyVariables(uploadResponse.data.empty_variables || 0);
                toast({ title: 'Success', description: 'CSV file uploaded successfully', status: 'success', duration: 3000, isClosable: true });
            } else {
                throw new Error('Upload failed');
            }
        } catch (error) {
            if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }
            setIsValidating(false);
            setValidationProgress(0);

            if (error.response?.status === 400 && error.response?.data?.validation_error) {
                const data = error.response.data;
                setCsvValidationErrors(data.validation_error);
                setTotalRows(data.totalRows || 0);
                setValidationError(data.error || 'CSV validation failed');
                toast({ title: 'Validation Failed', description: data.error || 'CSV validation failed', status: 'error', duration: 5000, isClosable: true });
            } else {
                const msg = error.response?.data?.message || error.message || 'Failed to upload CSV';
                setValidationError(msg);
                toast({ title: 'Error', description: msg, status: 'error', duration: 5000, isClosable: true });
            }
            throw error;
        }
    };

    const handleValidateAndContinue = async () => {
        if (!selectedTemplate || !uploadedFile) return;
        setIsValidating(true);
        setValidationProgress(0);
        try {
            const headers = csvColumns.map(col => col.name);
            await uploadCSV(uploadedFile, headers);
            const variables = extractVariables(selectedTemplate.message);
            const initialMappings = variables.map(variable => ({
                templateVariable: variable,
                csvColumn: csvColumns.find(col => col.name === variable)?.name || '',
                useStaticValue: false,
                staticValue: '',
            }));
            setVariableMappings(initialMappings);
            setCurrentStep(2);
        } catch {
            // stays on step 1, error already shown
        }
    };

    const handleUpdateMapping = (index, field, value) => {
        const updated = [...variableMappings];
        updated[index] = { ...updated[index], [field]: value };
        setVariableMappings(updated);
    };

    // ─── start sending ────────────────────────────────────────────────────────────
    const handleStartSending = async () => {
        setCurrentStep(3);
        setPendingCount(totalRows);
        setIsSending(true);

        startStatusPolling();
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/csv/msg-process`,
                {
                    csv_id: getActiveCsvId(),
                    template_id: selectedTemplate.id,
                    preferred_channels: selectedChannels,
                    // variable_mappings: variableMappings,
                },
                { headers: getApiHeaders() }
            );
            if (response.status === 200) {
                toast({ title: 'Campaign Started', description: 'Messages are being sent', status: 'success', duration: 3000, isClosable: true });
            } else {
                throw new Error('Failed to start campaign');
            }
        } catch (error) {
            const msg = error.response?.data?.message || error.message || 'Failed to start campaign';
            setIsSending(false);
            setCurrentStep(2);
            if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
            toast({ title: 'Error', description: msg, status: 'error', duration: 5000, isClosable: true });
        }
    };

    // ─── polling ──────────────────────────────────────────────────────────────────
    const startStatusPolling = () => {
        if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        fetchCampaignStatus();
        pollingIntervalRef.current = setInterval(fetchCampaignStatus, 2000);
    };

    const fetchCampaignStatus = async () => {
        try {
            const id = getActiveCsvId();
            if (!id) return;
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/csv/msg-status/${id}`,
                { headers: getApiHeaders() }
            );

            if (response.status === 200 && response.data?.data) {
                const s = response.data.data;
                const processedCount = s.process_count || 0;
                const successCount = s.success_count || 0;
                const errorCount = s.error_count || 0;
                const totalCount = s.total_rows || totalRows;

                setSentCount(successCount);
                setFailedCount(errorCount);
                setPendingCount(totalCount - processedCount);
                setQueueCount(s.que_count || 0);

                if (Array.isArray(s.logs) && s.logs.length > 0) {
                    setCampaignLogs(s.logs);
                }

                if (s.status_id === 'stopped' || s.status_id === 'paused') {
                    setIsPaused(true);
                    if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
                }
                if (s.status_id === 'running') setIsPaused(false);

                if (s.status_id === 'completed' || processedCount >= totalCount) {
                    setIsSending(false);
                    setIsCompleted(true);
                    if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
                    toast({ title: 'Campaign Completed', description: `Sent: ${successCount}, Failed: ${errorCount}`, status: 'success', duration: 5000, isClosable: true });
                }
            }
        } catch (err) {
            console.error('Status polling error:', err);
        }
    };

    // ─── pause / resume / cancel ──────────────────────────────────────────────────
    const handlePauseCampaign = async () => {
        setIsPauseLoading(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/csv/msg-stop`, { csv_id: getActiveCsvId() }, { headers: getApiHeaders() });
            setIsPaused(true);
            if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
            toast({ title: 'Campaign Paused', status: 'info', duration: 3000, isClosable: true });
        } catch (err) {
            toast({ title: 'Error', description: err.response?.data?.message || 'Failed to pause campaign', status: 'error', duration: 4000, isClosable: true });
        } finally {
            setIsPauseLoading(false);
        }
    };

    const handleResumeCampaign = async () => {
        setIsResumeLoading(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/csv/msg-resume`, { csv_id: getActiveCsvId() }, { headers: getApiHeaders() });
            setIsPaused(false);
            startStatusPolling();
            toast({ title: 'Campaign Resumed', status: 'success', duration: 3000, isClosable: true });
        } catch (err) {
            toast({ title: 'Error', description: err.response?.data?.message || 'Failed to resume campaign', status: 'error', duration: 4000, isClosable: true });
        } finally {
            setIsResumeLoading(false);
        }
    };

    const handleCancelCampaign = async () => {
        setIsCancelLoading(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/csv/msg-cancel`, { csv_id: getActiveCsvId() }, { headers: getApiHeaders() });
            if (pollingIntervalRef.current) { clearInterval(pollingIntervalRef.current); pollingIntervalRef.current = null; }
            toast({ title: 'Campaign Cancelled', status: 'warning', duration: 3000, isClosable: true });
            handleClose();
        } catch (err) {
            toast({ title: 'Error', description: err.response?.data?.message || 'Failed to cancel campaign', status: 'error', duration: 4000, isClosable: true });
        } finally {
            setIsCancelLoading(false);
        }
    };

    // ─── misc helpers ─────────────────────────────────────────────────────────────
    const downloadErrorReport = () => {
        if (!csvValidationErrors) return;
        const rows = ['Type,Row,Contact,Details'];
        (csvValidationErrors.duplicate_list || []).forEach(d => {
            rows.push(`Duplicate,${d.row},"${d.contact}",Duplicate contact number`);
        });
        (csvValidationErrors.empty_variable_list || []).forEach(e => {
            rows.push(`Empty Variable,${e.row},"${e.contact}","${(e.empty_fields || []).join(', ')}"`);
        });
        (csvValidationErrors.invalid_contact_list || []).forEach(d => {
            rows.push(`Invalid Contact,${d.row},"${d.contact}",Invalid contact number`);
        });
        const a = Object.assign(document.createElement('a'), {
            href: URL.createObjectURL(new Blob([rows.join('\n')], { type: 'text/csv' })),
            download: 'validation_errors.csv',
        });
        a.click();
    };

    const downloadSampleCSV = () => {
        const csv = `contact,name,tracking_id,amount,date\n+919876543210,John Doe,TRK123456,2500,15 Feb 2025\n+919876543211,Jane Smith,TRK123457,3500,16 Feb 2025`;
        const a = Object.assign(document.createElement('a'), {
            href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
            download: 'sample_campaign.csv',
        });
        a.click();
    };

    const getAvailableChannels = () => {
        if (!selectedTemplate?.channel_id) return [];
        try {
            const templateChannelIds = JSON.parse(selectedTemplate.channel_id);
            return channels.filter(ch =>
                templateChannelIds.includes(parseInt(ch.id))
            );
        } catch {
            return channels.filter(ch => ch.id === 'smart');
        }
    };

    // '' for plain-text templates — those keep the plain-text preview below.
    const templateHtml = useMemo(() => templateEmailHtml(selectedTemplate), [selectedTemplate]);

    const progressPercentage = totalRows > 0 ? ((sentCount + failedCount) / totalRows) * 100 : 0;
    const successRate = (sentCount + failedCount) > 0 ? (sentCount / (sentCount + failedCount)) * 100 : 0;

    // ─── render ───────────────────────────────────────────────────────────────────
    return (
        <>
        <Modal isOpen={isOpen} onClose={handleClose} size="2xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent maxH="90vh">
                <ModalHeader>
                    <Flex align="center" gap={2}>
                        <FiFileText size={20} color="var(--brand-primary)" />
                        <Text>New CSV Message Campaign</Text>
                    </Flex>
                    <Text fontSize="sm" fontWeight="normal" color="gray.600" mt={1}>
                        Upload a CSV file and send template-based messages
                    </Text>
                </ModalHeader>
                <ModalCloseButton />

                {/* Step Progress Indicator */}
                {currentStep > 1 && (
                    <Box borderBottom="1px" borderColor="gray.200" py={4} px={6}>
                        <Flex align="center" justify="center" gap={2}>
                            {[['Upload', 1], ['Channels & Mapping', 2], ['Send', 3]].map(([label, step], idx) => (
                                <React.Fragment key={step}>
                                    {idx > 0 && <Box w="64px" h="2px" bg="gray.200" />}
                                    <Flex align="center" gap={2} color={currentStep >= step ? 'var(--brand-primary)' : 'gray.400'}>
                                        <Flex
                                            w="32px" h="32px" borderRadius="full"
                                            bg={currentStep >= step ? 'var(--brand-primary)' : 'gray.200'}
                                            color={currentStep >= step ? 'white' : 'gray.400'}
                                            align="center" justify="center" fontWeight="medium"
                                        >
                                            {step}
                                        </Flex>
                                        <Text fontSize="sm" fontWeight="medium">{label}</Text>
                                    </Flex>
                                </React.Fragment>
                            ))}
                        </Flex>
                    </Box>
                )}

                <ModalBody>
                    {/* ── Step 1: Template & Upload ── */}
                    {currentStep === 1 && (
                        <VStack spacing={6} align="stretch">
                            {/* Campaign Name */}
                            <Box>
                                <FormLabel>Campaign Name *</FormLabel>
                                <Input
                                    placeholder="Enter campaign name"
                                    value={campaignName}
                                    onChange={(e) => setCampaignName(e.target.value)}
                                />
                            </Box>

                            {/* Template Selection */}
                            <Box>
                                <FormLabel>Select Template *</FormLabel>
                                <Select
                                    placeholder="Choose a template"
                                    value={selectedTemplate?.id || ''}
                                    onChange={(e) => {
                                        const t = templates.find(t => t.id === parseInt(e.target.value));
                                        setSelectedTemplate(t || null);
                                        setShowTemplatePreview(false);
                                        setSelectedChannels([]);
                                        setUploadedFile(null);
                                        setIsValidationComplete(false);
                                        setValidationError(null);
                                        setMissingVariables([]);
                                        setValidationProgress(0);
                                    }}
                                >
                                    {templates.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </Select>

                                {selectedTemplate && (
                                    <Card mt={3} bg="var(--brand-tint)" borderColor="var(--brand-tint-strong)" borderWidth="1px">
                                        <CardBody p={3}>
                                            <Text fontSize="md" fontWeight="medium" color="var(--brand-900)" mb={1}>
                                                {templateHtml ? 'Email Content:' : 'Template Message:'}
                                            </Text>
                                            {templateHtml ? (
                                                // An email template is a whole HTML document — open it on demand
                                                // instead of spending 360px of the wizard on it. The preview modal
                                                // renders it in an iframe, so it looks as designed and its CSS
                                                // cannot leak into the app.
                                                <Box
                                                    mb={2}
                                                    border="1px solid"
                                                    borderColor="var(--brand-border)"
                                                    borderRadius="md"
                                                    bg="white"
                                                    p={3}
                                                >
                                                    <Flex align="center" justify="space-between" gap={3} flexWrap="wrap">
                                                        <Text fontSize="sm" color="gray.600">
                                                            HTML email content · {templateHtml.length.toLocaleString()} characters
                                                        </Text>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            leftIcon={<FiEye />}
                                                            flexShrink={0}
                                                            onClick={() => setShowTemplatePreview(true)}
                                                        >
                                                            Preview HTML
                                                        </Button>
                                                    </Flex>
                                                </Box>
                                            ) : (
                                                <Text fontSize="sm" color="var(--brand-primary-hover)" mb={2}>{selectedTemplate.message}</Text>
                                            )}
                                            <Text fontSize="xs" fontWeight="medium" color="var(--brand-900)" mb={1}>Template Variables:</Text>
                                            <Flex flexWrap="wrap" gap={1.5}>
                                                {extractVariables(selectedTemplate.message).map(v => (
                                                    <Tag key={v} bg="white" color="var(--brand-primary-hover)" border="1px solid var(--brand-border)" fontSize="xs">{`$${v}`}</Tag>
                                                ))}
                                            </Flex>
                                        </CardBody>
                                    </Card>
                                )}
                            </Box>

                            <Divider />

                            {/* CSV Upload */}
                            <Box>
                                <FormLabel>Upload CSV File *</FormLabel>
                                <Box
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    border="2px dashed"
                                    borderColor={isDragging ? 'var(--brand-primary)' : validationError ? 'red.300' : 'gray.300'}
                                    borderRadius="lg" p={8} textAlign="center"
                                    bg={isDragging ? 'blue.50' : validationError ? 'red.50' : 'white'}
                                    transition="all 0.2s"
                                >
                                    <Input type="file" accept=".csv" onChange={handleFileSelect} display="none" id="csv-upload" />
                                    {uploadedFile ? (
                                        <VStack spacing={2}>
                                            <FiFileText size={48} color={validationError ? '#E53E3E' : 'var(--brand-primary)'} />
                                            <Text fontWeight="medium">{uploadedFile.name}</Text>
                                            <Text fontSize="xs" color="gray.600">{(uploadedFile.size / 1024).toFixed(2)} KB</Text>
                                            <Button variant="outline" size="sm" mt={2}
                                                onClick={() => {
                                                    setUploadedFile(null);
                                                    setIsValidationComplete(false);
                                                    setValidationError(null);
                                                    setMissingVariables([]);
                                                    setCsvValidationErrors(null);
                                                    setValidationProgress(0);
                                                    if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }
                                                }}>
                                                Remove File
                                            </Button>
                                        </VStack>
                                    ) : (
                                        <>
                                            <FiUpload size={48} color="gray" style={{ margin: '0 auto 12px' }} />
                                            <Text fontWeight="medium" mb={1}>Drag and drop your CSV file here</Text>
                                            <Text fontSize="xs" color="gray.600" mb={3}>or</Text>
                                            <Button variant="outline" size="sm"
                                                onClick={() => document.getElementById('csv-upload')?.click()}
                                                isDisabled={!selectedTemplate}>
                                                Browse Files
                                            </Button>
                                            <Text fontSize="xs" color="gray.600" mt={3}>Supported: .csv • Max size: 10MB</Text>
                                            {!selectedTemplate && (
                                                <Text fontSize="xs" color="orange.600" mt={2}>Please select a template first</Text>
                                            )}
                                        </>
                                    )}
                                </Box>
                                <Button variant="link" size="sm" color="var(--brand-primary)" mt={2} onClick={downloadSampleCSV}>
                                    Download Sample CSV
                                </Button>
                            </Box>

                            {/* Missing Variables */}
                            {missingVariables.length > 0 && (
                                <Alert status="error" borderRadius="md" bg="red.50" border="1px solid" borderColor="red.400">
                                    <AlertIcon as={FiXCircle} />
                                    <Box flex="1">
                                        <AlertTitle>Missing Variables in CSV</AlertTitle>
                                        <AlertDescription fontSize="sm" mt={1}>
                                            The following variables are required but not found in your CSV:
                                            <Flex flexWrap="wrap" gap={2} mt={2}>
                                                {missingVariables.map(v => (
                                                    <Tag key={v} colorScheme="red" fontSize="xs">${v}</Tag>
                                                ))}
                                            </Flex>
                                            <Text mt={2}>Please add these columns to your CSV file and upload again.</Text>
                                        </AlertDescription>
                                    </Box>
                                </Alert>
                            )}

                            {/* Validation Progress */}
                            {uploadedFile && isValidating && (
                                <Alert status="info" borderRadius="md" bg="blue.50" border="1px solid" borderColor="blue.500">
                                    <AlertIcon><FiActivity /></AlertIcon>
                                    <Box flex="1">
                                        <AlertTitle>Processing CSV File...</AlertTitle>
                                        <Progress value={validationProgress} size="sm"
                                            sx={{ '& > div': { background: 'var(--brand-primary)' } }}
                                            bg="gray.200" mt={2} borderRadius="full" mb={1} />
                                        <AlertDescription fontSize="xs">
                                            Uploading and validating... {validationProgress}%
                                        </AlertDescription>
                                    </Box>
                                </Alert>
                            )}

                            {/* Validation Complete */}
                            {uploadedFile && isValidationComplete && !validationError && (
                                <Alert status="success" borderRadius="md" bg="#f0fdf4" border="1px solid #b9f8cf">
                                    <AlertIcon as={FiCheckCircle} />
                                    <Box flex="1">
                                        <AlertTitle>Validation Complete</AlertTitle>
                                        <AlertDescription fontSize="xs">File validated successfully. Ready to proceed to mapping.</AlertDescription>
                                    </Box>
                                </Alert>
                            )}

                            {/* Validation Error */}
                            {validationError && !isValidating && (
                                <Alert status="error" borderRadius="md" bg="red.50" border="1px solid" borderColor="red.400">
                                    <AlertIcon as={FiXCircle} />
                                    <Box flex="1">
                                        <AlertTitle>Validation Failed</AlertTitle>
                                        <AlertDescription fontSize="xs">{validationError}</AlertDescription>
                                    </Box>
                                </Alert>
                            )}

                            {/* CSV Validation Errors (from API 400) */}
                            {csvValidationErrors && !isValidating && (
                                <Box borderRadius="md" border="1px solid" borderColor="red.300" overflow="hidden">
                                    {/* Header */}
                                    <Flex bg="red.50" px={4} py={3} align="center" gap={2}>
                                        <FiAlertTriangle color="#C53030" />
                                        <Text fontWeight="semibold" color="red.700" fontSize="sm">CSV Validation Issues</Text>
                                        <Button
                                            size="xs" variant="outline" colorScheme="red" ml="auto"
                                            leftIcon={<FiDownload size={12} />}
                                            onClick={downloadErrorReport}
                                        >
                                            Download Error Report
                                        </Button>
                                    </Flex>

                                    {/* Summary counts */}
                                    <Grid templateColumns="repeat(4, 1fr)" px={4} py={3} bg="white" borderBottom="1px solid" borderColor="red.100">
                                        {[
                                            { label: 'Total Rows', value: totalRows, color: 'gray.700' },
                                            { label: 'Duplicates', value: csvValidationErrors.duplicates || 0, color: 'orange.600' },
                                            { label: 'Invalid Contacts', value: csvValidationErrors.invalid_contact || 0, color: 'purple.600' },
                                            { label: 'Empty Variables', value: csvValidationErrors.empty_variable || 0, color: 'red.600' },
                                        ].map(({ label, value, color }) => (
                                            <Box key={label} textAlign="center" py={1}>
                                                <Text fontSize="xl" fontWeight="semibold" color={color}>{value}</Text>
                                                <Text fontSize="xs" color="gray.500">{label}</Text>
                                            </Box>
                                        ))}
                                    </Grid>

                                    {/* Duplicates detail */}
                                    {csvValidationErrors.duplicates > 0 && csvValidationErrors.duplicate_list?.length > 0 && (
                                        <Box borderTop="1px solid" borderColor="red.100">
                                            <Flex
                                                px={4} py={2} align="center" cursor="pointer"
                                                bg="orange.50" _hover={{ bg: 'orange.100' }}
                                                onClick={() => setShowDuplicateDetails(v => !v)}
                                            >
                                                <Tag colorScheme="orange" size="sm" mr={2}>Duplicates</Tag>
                                                <Text fontSize="sm" color="orange.700">
                                                    {csvValidationErrors.duplicates} duplicate contact{csvValidationErrors.duplicates !== 1 ? 's' : ''}
                                                </Text>
                                                <Box ml="auto">{showDuplicateDetails ? <FiChevronUp /> : <FiChevronDown />}</Box>
                                            </Flex>
                                            <Collapse in={showDuplicateDetails}>
                                                <Box maxH="180px" overflowY="auto">
                                                    <Grid templateColumns="60px 1fr" fontSize="xs" fontWeight="semibold" color="gray.500"
                                                        px={4} py={2} bg="gray.50" position="sticky" top={0}>
                                                        <Text>Row</Text><Text>Contact</Text>
                                                    </Grid>
                                                    {csvValidationErrors.duplicate_list.map((d, i) => (
                                                        <Grid key={i} templateColumns="60px 1fr" fontSize="xs" px={4} py={1.5}
                                                            bg={i % 2 === 0 ? 'white' : 'gray.50'}>
                                                            <Text color="gray.500">#{d.row}</Text>
                                                            <Text fontFamily="mono">{d.contact || '—'}</Text>
                                                        </Grid>
                                                    ))}
                                                </Box>
                                            </Collapse>
                                        </Box>
                                    )}

                                    {/* Invalid contacts detail */}
                                    {csvValidationErrors.invalid_contact > 0 && csvValidationErrors.invalid_contact_list?.length > 0 && (
                                        <Box borderTop="1px solid" borderColor="red.100">
                                            <Flex
                                                px={4} py={2} align="center" cursor="pointer"
                                                bg="purple.50" _hover={{ bg: 'purple.100' }}
                                                onClick={() => setShowInvalidDetails(v => !v)}
                                            >
                                                <Tag colorScheme="purple" size="sm" mr={2}>Invalid Contacts</Tag>
                                                <Text fontSize="sm" color="purple.700">
                                                    {csvValidationErrors.invalid_contact} invalid contact{csvValidationErrors.invalid_contact !== 1 ? 's' : ''}
                                                </Text>
                                                <Box ml="auto">{showInvalidDetails ? <FiChevronUp /> : <FiChevronDown />}</Box>
                                            </Flex>
                                            <Collapse in={showInvalidDetails}>
                                                <Box maxH="180px" overflowY="auto">
                                                    <Grid templateColumns="60px 1fr" fontSize="xs" fontWeight="semibold" color="gray.500"
                                                        px={4} py={2} bg="gray.50" position="sticky" top={0}>
                                                        <Text>Row</Text><Text>Contact</Text>
                                                    </Grid>
                                                    {csvValidationErrors.invalid_contact_list.slice(0, 10).map((d, i) => (
                                                        <Grid key={i} templateColumns="60px 1fr" fontSize="xs" px={4} py={1.5}
                                                            bg={i % 2 === 0 ? 'white' : 'gray.50'}>
                                                            <Text color="gray.500">#{d.row}</Text>
                                                            <Text fontFamily="mono">{d.contact || '—'}</Text>
                                                        </Grid>
                                                    ))}
                                                    {csvValidationErrors.invalid_contact_list.length > 10 && (
                                                        <Flex px={4} py={2} align="center" bg="purple.50" borderTop="1px solid" borderColor="purple.100">
                                                            <Text fontSize="xs" color="purple.600">
                                                                +{csvValidationErrors.invalid_contact_list.length - 10} more rows — download the error report for the full list.
                                                            </Text>
                                                        </Flex>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </Box>
                                    )}

                                    {/* Empty variables detail */}
                                    {csvValidationErrors.empty_variable > 0 && csvValidationErrors.empty_variable_list?.length > 0 && (
                                        <Box borderTop="1px solid" borderColor="red.100">
                                            <Flex
                                                px={4} py={2} align="center" cursor="pointer"
                                                bg="red.50" _hover={{ bg: 'red.100' }}
                                                onClick={() => setShowEmptyVarDetails(v => !v)}
                                            >
                                                <Tag colorScheme="red" size="sm" mr={2}>Empty Variables</Tag>
                                                <Text fontSize="sm" color="red.700">
                                                    {csvValidationErrors.empty_variable} row{csvValidationErrors.empty_variable !== 1 ? 's' : ''} with empty fields
                                                </Text>
                                                <Box ml="auto">{showEmptyVarDetails ? <FiChevronUp /> : <FiChevronDown />}</Box>
                                            </Flex>
                                            <Collapse in={showEmptyVarDetails}>
                                                <Box maxH="220px" overflowY="auto">
                                                    <Grid templateColumns="60px 1fr 1fr" fontSize="xs" fontWeight="semibold" color="gray.500"
                                                        px={4} py={2} bg="gray.50" position="sticky" top={0}>
                                                        <Text>Row</Text><Text>Contact</Text><Text>Empty Fields</Text>
                                                    </Grid>
                                                    {csvValidationErrors.empty_variable_list.slice(0, 10).map((e, i) => (
                                                        <Grid key={i} templateColumns="60px 1fr 1fr" fontSize="xs" px={4} py={1.5}
                                                            bg={i % 2 === 0 ? 'white' : 'gray.50'} alignItems="center">
                                                            <Text color="gray.500">#{e.row}</Text>
                                                            <Text fontFamily="mono">{e.contact || '—'}</Text>
                                                            <Flex flexWrap="wrap" gap={1}>
                                                                {(e.empty_fields || []).map(f => (
                                                                    <Tag key={f} colorScheme="red" size="sm" fontSize="xs">{f}</Tag>
                                                                ))}
                                                            </Flex>
                                                        </Grid>
                                                    ))}
                                                    {csvValidationErrors.empty_variable_list.length > 10 && (
                                                        <Flex px={4} py={2} align="center" bg="red.50" borderTop="1px solid" borderColor="red.100">
                                                            <Text fontSize="xs" color="red.600">
                                                                +{csvValidationErrors.empty_variable_list.length - 10} more rows — download the error report for the full list.
                                                            </Text>
                                                        </Flex>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </Box>
                                    )}

                                    <Box px={4} py={2} bg="red.50" borderTop="1px solid" borderColor="red.100">
                                        <Text fontSize="xs" color="red.600">Fix these issues in your CSV and upload again.</Text>
                                    </Box>
                                </Box>
                            )}

                            {/* Format Hint */}
                            <Alert status="warning" borderRadius="md" bg="#fffbeb" border="1px solid #fee685">
                                <AlertIcon as={FiAlertCircle} />
                                <Box>
                                    <AlertTitle fontSize="sm" color="#7b3306">CSV Format Requirements:</AlertTitle>
                                    <AlertDescription fontSize="xs" mt={1}>
                                        <Text color="#7b3306"><strong>Required:</strong> contact column and all template variables</Text>
                                        <Text mt={2} color="#7b3306"><strong>Note:</strong></Text>
                                        <Box as="ul" pl={5} mt={1} color="#7b3306">
                                            <li>Column names must match template variables exactly</li>
                                            <li>Column names must not contain spaces</li>
                                            <li>First row must contain headers</li>
                                        </Box>
                                    </AlertDescription>
                                </Box>
                            </Alert>
                        </VStack>
                    )}

                    {/* ── Step 2: Channels & Mapping ── */}
                    {currentStep === 2 && (
                        <VStack spacing={6} align="stretch">

                            {/* Delivery Channels (replaces old variable mapping card) */}
                            <Card>
                                <CardBody>
                                    <DeliveryChannels
                                        channels={getAvailableChannels()}
                                        selectedChannels={selectedChannels}
                                        setSelectedChannels={setSelectedChannels}
                                    />
                                </CardBody>
                            </Card>

                            {/* CSV Columns */}
                            <Card>
                                <CardBody>
                                    <Text fontSize="lg" fontWeight={400} mb={4}>
                                        <Flex align="center" gap={2}>
                                            <FiFileText color="var(--brand-primary)" />
                                            CSV Columns Detected
                                        </Flex>
                                    </Text>
                                    <Box bg="gray.50" border="1px" borderColor="gray.200" borderRadius="lg" p={3}>
                                        <Flex flexWrap="wrap" gap={2}>
                                            {csvColumns.map(col => (
                                                <Tag key={col.name} align="center" gap={1.5} bg="white" border="1px" borderColor="gray.200" borderRadius="md" py={2}>
                                                    <Text fontSize="sm" fontWeight="medium">{col.name}</Text>
                                                    {col.name === 'contact' && (
                                                        <Tag bg="var(--brand-primary)" color="white" fontSize="xs" size="sm">Required</Tag>
                                                    )}
                                                </Tag>
                                            ))}
                                        </Flex>
                                        <Text fontSize="xs" color="gray.600" mt={3}>Total columns: {csvColumns.length}</Text>
                                    </Box>
                                </CardBody>
                            </Card>

                            {/* Validation Summary */}
                            <Card borderWidth="1px" borderColor="#0000001a">
                                <CardBody>
                                    <Text fontSize="lg" fontWeight={400} mb={4}>Validation Summary</Text>
                                    <Grid templateColumns="repeat(4, 1fr)" gap={4}>
                                        {[
                                            { label: 'Total Rows', value: totalRows, icon: FiUsers, bg: 'blue.50', color: 'blue.600' },
                                            { label: 'Invalid Contacts', value: invalidContacts, icon: FiXCircle, bg: 'red.50', color: 'red.600' },
                                            { label: 'Duplicates', value: duplicates, icon: FiAlertCircle, bg: 'orange.50', color: 'orange.600' },
                                            { label: 'Empty Variables', value: emptyVariables, icon: FiAlertTriangle, bg: 'yellow.50', color: 'yellow.600' },
                                        ].map(({ label, value, icon: Icon, bg, color }) => (
                                            <Flex key={label} align="center" gap={3}>
                                                <Flex w="40px" h="40px" bg={bg} borderRadius="lg" align="center" justify="center">
                                                    <Icon size={20} color={color} />
                                                </Flex>
                                                <Box>
                                                    <Text fontSize="xs" color="gray.600">{label}</Text>
                                                    <Text fontSize="lg" fontWeight="semibold" color={label === 'Total Rows' ? undefined : color}>{value}</Text>
                                                </Box>
                                            </Flex>
                                        ))}
                                    </Grid>
                                    <Alert status="warning" mt={4} borderRadius="md" bg="#fffbeb" border="1px solid #fee685" color="#973c00">
                                        <AlertIcon as={FiAlertTriangle} />
                                        <AlertDescription fontSize="xs">
                                            <strong>Warning:</strong> Messages will be sent immediately once confirmed. Please ensure all mappings are correct before proceeding.
                                        </AlertDescription>
                                    </Alert>
                                </CardBody>
                            </Card>

                            {/* Activity Log — only meaningful for a resumed draft */}
                            {campaignLogs.length > 0 && (
                                <CampaignActivityLog
                                    logs={campaignLogs}
                                    isExpanded={showLogs}
                                    onToggle={() => setShowLogs(v => !v)}
                                />
                            )}
                        </VStack>
                    )}

                    {/* ── Step 3: Sending Progress ── */}
                    {currentStep === 3 && !isCompleted && (
                        <VStack spacing={6} align="stretch">
                            {/* Campaign Summary */}
                            <Box className='box-style-card'>
                                <Text fontSize="lg" fontWeight={400} mb={4}>Campaign Summary</Text>
                                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                    <Box>
                                        <Text fontSize="xs" color="gray.600" mb={1}>Template Name</Text>
                                        <Text fontWeight="medium">{selectedTemplate?.name}</Text>
                                    </Box>
                                    {/* <Box>
                                        <Text fontSize="xs" color="gray.600" mb={1}>Channel(s)</Text>
                                        <Flex flexWrap="wrap" gap={1}>
                                            {(existingCampaign
                                                ? [existingCampaign?.preferred_channel]
                                                : getOrderedChannelNames()
                                            ).map((name, i) => (
                                                <Tag key={i} fontSize="xs">{name}</Tag>
                                            ))}
                                        </Flex>
                                    </Box> */}
                                    <Box>
                                        <Text fontSize="xs" color="gray.600" mb={1}>Total Contacts</Text>
                                        <Text fontWeight="medium">{totalRows}</Text>
                                    </Box>
                                    {/* <Box>
                                        <Text fontSize="xs" color="gray.600" mb={1}>Estimated Duration</Text>
                                        <Text fontWeight="medium">
                                            {existingCampaign
                                                ? existingCampaign.estimated_time
                                                : `~${Math.ceil(totalRows / 10)} Minutes`}
                                        </Text>
                                    </Box> */}
                                </Grid>
                            </Box>

                            {/* Progress Bar */}
                            <Box className='box-style-card'>
                                <Flex justify="space-between" mb={2}>
                                    <Text fontSize="lg" fontWeight={400} mb={4}>Sending Progress</Text>
                                    <Text fontSize="sm" color="gray.600">{progressPercentage.toFixed(1)}%</Text>
                                </Flex>
                                <Progress value={progressPercentage} size="sm" colorScheme="blue" mb={4} />
                                <Grid templateColumns="repeat(5, 1fr)" gap={4} textAlign="center">
                                    <Box>
                                        <Text fontSize="2xl" fontWeight="400" color="green.600">{sentCount}</Text>
                                        <Text fontSize="xs" color="gray.600">Sent</Text>
                                    </Box>
                                    <Box>
                                        <Text fontSize="2xl" fontWeight="400" color="red.600">{failedCount}</Text>
                                        <Text fontSize="xs" color="gray.600">Failed</Text>
                                    </Box>
                                    <Box>
                                        <Text fontSize="2xl" fontWeight="400" color="purple.600">{queueCount}</Text>
                                        <Text fontSize="xs" color="gray.600">In Queue</Text>
                                    </Box>
                                    <Box>
                                        <Text fontSize="2xl" fontWeight="400" color="orange.600">{pendingCount}</Text>
                                        <Text fontSize="xs" color="gray.600">Pending</Text>
                                    </Box>
                                    <Box>
                                        <Text fontSize="2xl" fontWeight="400" color="var(--brand-primary)">{successRate.toFixed(1)}%</Text>
                                        <Text fontSize="xs" color="gray.600">Success Rate</Text>
                                    </Box>
                                </Grid>
                            </Box>

                            {/* Activity Log */}
                            <CampaignActivityLog
                                logs={campaignLogs}
                                isExpanded={showLogs}
                                onToggle={() => setShowLogs(v => !v)}
                            />

                            {/* Controls */}
                            {(existingCampaign?.status_id === "running" || existingCampaign?.status_id === "paused" || !existingCampaign) && (
                                <Flex justify="center" gap={3} my={4}>
                                    <Button
                                        variant="outline"
                                        isDisabled={!isSending || isCancelLoading}
                                        isLoading={isPaused ? isResumeLoading : isPauseLoading}
                                        loadingText={isPaused ? 'Resuming...' : 'Pausing...'}
                                        leftIcon={isPaused ? <FiPlay /> : <FiPause />}
                                        onClick={isPaused ? handleResumeCampaign : handlePauseCampaign}
                                    >
                                        {isPaused ? 'Resume' : 'Pause'}
                                    </Button>
                                    <Button
                                        colorScheme="red"
                                        isDisabled={!isSending || isPauseLoading || isResumeLoading}
                                        isLoading={isCancelLoading}
                                        loadingText="Cancelling..."
                                        leftIcon={<FiX />}
                                        onClick={handleCancelCampaign}
                                    >
                                        Cancel Campaign
                                    </Button>
                                </Flex>
                            )}

                        </VStack>
                    )}

                    {/* ── Completion Screen ── */}
                    {currentStep === 3 && isCompleted && (
                        <VStack spacing={6} py={8}>
                            <Box textAlign="center">
                                <Flex w="64px" h="64px" bg="green.50" borderRadius="full" align="center" justify="center" mx="auto" mb={4}>
                                    <FiCheckCircle size={40} color="#38A169" />
                                </Flex>
                                <Text fontSize="lg" fontWeight={400} mb={4}>Campaign Completed Successfully!</Text>
                                <Text color="gray.600">Your messages have been sent to all contacts</Text>
                            </Box>
                            <Card w="full">
                                <CardBody>
                                    <Grid templateColumns="repeat(3, 1fr)" gap={6} textAlign="center">
                                        <Box>
                                            <Text fontSize="3xl" fontWeight="400" color="green.600">{sentCount}</Text>
                                            <Text fontSize="sm" color="gray.600" mt={1}>Successfully Sent</Text>
                                        </Box>
                                        <Box>
                                            <Text fontSize="3xl" fontWeight="400" color="red.600">{failedCount}</Text>
                                            <Text fontSize="sm" color="gray.600" mt={1}>Failed</Text>
                                        </Box>
                                        <Box>
                                            <Text fontSize="3xl" fontWeight="400" color="var(--brand-primary)">{successRate.toFixed(1)}%</Text>
                                            <Text fontSize="sm" color="gray.600" mt={1}>Success Rate</Text>
                                        </Box>
                                    </Grid>
                                </CardBody>
                            </Card>

                            {/* Activity Log */}
                            <Box w="full">
                                <CampaignActivityLog
                                    logs={campaignLogs}
                                    isExpanded={showLogs}
                                    onToggle={() => setShowLogs(v => !v)}
                                />
                            </Box>

                            <Flex gap={3} justifyContent="flex-end">
                                <Button variant="outline" onClick={handleClose}>Close</Button>
                            </Flex>
                        </VStack>
                    )}
                </ModalBody>

                {/* Footer */}
                {currentStep < 3 && (
                    <ModalFooter borderTop="1px" borderColor="gray.200">
                        {currentStep === 1 ? (
                            <>
                                <Button variant="outline" mr={3} onClick={handleClose}>Cancel</Button>
                                <Button
                                    bg="var(--brand-primary)" color="white" _hover={{ bg: 'var(--brand-primary-hover)' }}
                                    isDisabled={!campaignName.trim() || !selectedTemplate || !uploadedFile || !isValidationComplete}
                                    isLoading={isValidating}
                                    loadingText="Uploading..."
                                    onClick={handleValidateAndContinue}
                                    rightIcon={<FiArrowRight />}
                                >
                                    Continue
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="outline" mr={3} leftIcon={<FiArrowLeft />}
                                    onClick={() => {
                                        setUploadedFile(null);
                                        setUploadedFileData(null);
                                        setIsValidationComplete(false);
                                        setValidationError(null);
                                        setValidationProgress(0);
                                        setMissingVariables([]);
                                        setCsvValidationErrors(null);
                                        setCsvColumns([]);
                                        setVariableMappings([]);
                                        setTotalRows(0);
                                        setInvalidContacts(0);
                                        setDuplicates(0);
                                        setEmptyVariables(0);
                                        setCurrentStep(1);
                                    }}
                                    disabled={!!existingCampaign}
                                >
                                    Back to Upload
                                </Button>
                                <Button
                                    bg="var(--brand-primary)" color="white" _hover={{ bg: 'var(--brand-primary-hover)' }}
                                    isDisabled={selectedChannels.length === 0}
                                    onClick={handleStartSending}
                                    leftIcon={<FiSend />}
                                >
                                    Start Sending
                                </Button>
                            </>
                        )}
                    </ModalFooter>
                )}
            </ModalContent>
        </Modal>

        {/* Opened from "Preview HTML" in step 1. Stacks over the wizard, which stays
            open behind it, so closing returns the author to the same step. */}
        <EmailPreviewModal
            isEmailPreviewOpen={showTemplatePreview}
            onEmailPreviewClose={() => setShowTemplatePreview(false)}
            subject={selectedTemplate?.subject || selectedTemplate?.email_subject || ''}
            body={templateHtml}
        />
        </>
    );
}